import React from 'react';
import { cn } from '../../lib/utils';

/**
 * DetailContainer - Reusable responsive container for detail/view pages
 * Ensures consistent responsive behavior across all detail views
 *
 * Features:
 * - Proper viewport constraints
 * - Responsive typography and padding
 * - Stacked layout on mobile, multi-column on desktop
 * - Consistent spacing throughout
 *
 * @param {Object} props
 * @param {ReactNode} props.children - Content to render inside container
 * @param {string} props.className - Additional CSS classes
 */
const DetailContainer = ({ children, className }) => {
  return (
    <div
      className={cn(
        "flex flex-col gap-4 sm:gap-6 p-3 sm:p-4 md:p-6 lg:p-8 w-full max-w-full overflow-hidden",
        className
      )}
    >
      {children}
    </div>
  );
};

/**
 * DetailContainer.Header - Header section with back button, title, and actions
 */
DetailContainer.Header = ({ children, className }) => {
  return (
    <div
      className={cn(
        "flex flex-col gap-4 md:flex-row md:items-center md:justify-between w-full max-w-full overflow-hidden",
        className
      )}
    >
      {children}
    </div>
  );
};

/**
 * DetailContainer.TitleSection - Left side of header with back button and title
 */
DetailContainer.TitleSection = ({ children, className }) => {
  return (
    <div
      className={cn(
        "flex items-center gap-2 sm:gap-4 min-w-0 flex-1 overflow-hidden",
        className
      )}
    >
      {children}
    </div>
  );
};

/**
 * DetailContainer.Title - Page title with responsive text size
 */
DetailContainer.Title = ({ title, subtitle, className }) => {
  return (
    <div className={cn("min-w-0 flex-1", className)}>
      <h1 className="text-2xl sm:text-3xl font-bold break-words">{title}</h1>
      {subtitle && (
        <p className="text-xs sm:text-sm text-muted-foreground break-words mt-1">
          {subtitle}
        </p>
      )}
    </div>
  );
};

/**
 * DetailContainer.Actions - Action buttons in header
 */
DetailContainer.Actions = ({ children, className }) => {
  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-2",
        className
      )}
    >
      {children}
    </div>
  );
};

/**
 * DetailContainer.Section - Content section with optional title
 */
DetailContainer.Section = ({ title, children, className }) => {
  return (
    <div className={cn("space-y-3 sm:space-y-4", className)}>
      {title && (
        <h2 className="text-lg sm:text-xl font-semibold">{title}</h2>
      )}
      {children}
    </div>
  );
};

/**
 * DetailContainer.Grid - Responsive grid that stacks on mobile
 * Defaults to 1 column on mobile, 2 on md, 3 on lg
 */
DetailContainer.Grid = ({ columns = 2, children, className }) => {
  const gridClass = {
    1: "grid-cols-1",
    2: "grid-cols-1 md:grid-cols-2",
    3: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
    4: "grid-cols-1 md:grid-cols-2 lg:grid-cols-4"
  }[columns] || "grid-cols-1 md:grid-cols-2";

  return (
    <div
      className={cn(
        "grid gap-3 sm:gap-4 md:gap-6",
        gridClass,
        className
      )}
    >
      {children}
    </div>
  );
};

/**
 * DetailContainer.Field - Individual field display with label and value
 */
DetailContainer.Field = ({ label, value, children, className }) => {
  return (
    <div className={cn("space-y-1", className)}>
      <p className="text-xs sm:text-sm font-medium text-muted-foreground">
        {label}
      </p>
      {children || (
        <p className="text-sm sm:text-base text-foreground break-all">
          {value || 'N/A'}
        </p>
      )}
    </div>
  );
};

/**
 * DetailContainer.Card - Card wrapper with responsive padding
 */
DetailContainer.Card = ({ children, className }) => {
  return (
    <div
      className={cn(
        "border rounded-lg p-3 sm:p-4 md:p-6 bg-card w-full max-w-full overflow-hidden",
        className
      )}
    >
      {children}
    </div>
  );
};

export default DetailContainer;
